import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  ECharts, EChartsOption, BarSeriesOption, LineSeriesOption,
  YAXisComponentOption, XAXisComponentOption, TitleComponentOption,
  LegendComponentOption, TooltipComponentOption, GridComponentOption,
  ECElementEvent,
  Payload,
  ToolboxComponentOption
} from 'echarts';
import { NgxEchartsDirective, ThemeOption } from 'ngx-echarts';
import { Subject, Subscription } from 'rxjs';

type MixedSeriesOption = BarSeriesOption | LineSeriesOption;

@Component({
  selector: 'app-bar-line-double-y-echart',
  templateUrl: './bar-line-double-y-echart.component.html',
  styleUrl: './bar-line-double-y-echart.component.css',
})
export class BarLineDoubleYEchartComponent implements OnInit, OnDestroy {
  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this._chartInstance) {
      this._chartInstance.resize();
    }
  }

  private _chartInstance: ECharts;

  @ViewChild(NgxEchartsDirective) plotRef: NgxEchartsDirective;

  @Input() set title(value: TitleComponentOption) {
    this.options.title = { ...value };
    this._plot();
  }
  @Input() set legend(value: LegendComponentOption) {
    this.options.legend = { ...value };
    this._plot();
  }
  @Input() set tooltip(value: TooltipComponentOption) {
    this.options.tooltip = { ...value };
    this._plot();
  }
  @Input() set xAxis(value: XAXisComponentOption) {
    this.options.xAxis = { ...value };
    this._plot();
  }
  @Input() set grid(value: GridComponentOption) {
    this.options.grid = { ...value };
    this._plot();
  }

  @Input() set yAxis(value: YAXisComponentOption[]) {
    this.options.yAxis = value;
    this._plot();
  }

  @Input() set series(list: MixedSeriesOption[]) {
    this.options.series = list;
    this._plot();
  }

  @Input() theme: string | ThemeOption;
  @Input() loading: boolean = false;
  @Input() merge: EChartsOption;

  @Input() autoResize: boolean;

  @Input() initOpts: {
    devicePixelRatio?: number;
    renderer?: string;
    width?: number | string;
    height?: number | string;
    locale?: string;
  } = {
      width: 'auto',
      height: 'auto'
    };

  @Input()
  set dispatchAction(payload: Payload) {
    this._chartRenderedSubscription = this.chartRendered
      .subscribe({
        next: () => {
          this._chartInstance?.dispatchAction(payload);
        },
        error: (errorResponse: HttpErrorResponse) => { }
      });
  }

  @Input()
  set toolbox(value: ToolboxComponentOption) {
    (<Record<string, any>>this.options.toolbox) = { ...value };
    this._plot();
  }

  @Input() loadingOpts: object = null; // Please refer https://github.com/xieziyu/ngx-echarts or https://echarts.apache.org/en/api.html#echartsInstance.showLoading

  @Output() chartInit = new Subject<ECharts>();

  @Output() chartClick = new Subject<ECElementEvent>();

  @Output() chartDblClick = new Subject<ECElementEvent>();

  @Output() chartMouseDown = new Subject<ECElementEvent>();

  @Output() chartMouseMove = new Subject<ECElementEvent>();

  @Output() chartMouseUp = new Subject<ECElementEvent>();

  @Output() chartMouseOver = new Subject<ECElementEvent>();

  @Output() chartMouseOut = new Subject<ECElementEvent>();

  @Output() chartRendered = new Subject<boolean>();

  @Output() chartFinished = new Subject<boolean>();

  @Output() dataZoom = new Subject<ECElementEvent>();

  options: EChartsOption = {
    title: {} as TitleComponentOption,
    legend: {} as LegendComponentOption,
    series: [] as BarSeriesOption | LineSeriesOption,
    tooltip: {} as TooltipComponentOption,
    xAxis: {} as XAXisComponentOption,
    yAxis: {} as YAXisComponentOption,
    grid: {} as GridComponentOption,
  };

  private _plot() {
    this.plotRef?.refreshChart();
    this._setLoader(true);
  }

  private _chartRenderedSubscription: Subscription;

  onChartInit(value: ECharts) {
    this._chartInstance = value;
    this.chartInit.next(value);
  }

  /**
  * Determines whether chart click on
  * @param value
  */
  onChartClick(value: unknown) {
    this.chartClick.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart double click on
   * @param value
   */
  onChartDblClick(value: unknown) {
    this.chartDblClick.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse down on
   * @param value
   */
  onChartMouseDown(value: unknown) {
    this.chartMouseDown.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse move on
   * @param value
   */
  onChartMouseMove(value: unknown) {
    this.chartMouseMove.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse up on
   * @param value
   */
  onChartMouseUp(value: unknown) {
    this.chartMouseUp.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse over on
   * @param value
   */
  onChartMouseOver(value: unknown) {
    this.chartMouseOver.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart mouse out on
   * @param value
   */
  onChartMouseOut(value: unknown) {
    this.chartMouseOut.next(value as ECElementEvent);
  }

  /**
   * Determines whether chart rendered on
   */
  onChartRendered() {
    this.chartRendered.next(true);
    this._setLoader(false);
  }

  /**
   * Determines whether chart finished on
   */
  onChartFinished() {
    this.chartFinished.next(true);
  }

  /**
 * Sets loader
 * @param option
 */
  private _setLoader(option: boolean) {
    this.loading = option;
  }

  /**
 * Determines whether data zoom on
 * @param value 
 */
  onDataZoom(value: unknown) {
    this.dataZoom.next(value as ECElementEvent);
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this._chartRenderedSubscription?.unsubscribe();
  }
}